import {
  Pagination,
  Spin,
  Checkbox,
  Empty,
  Button,
} from "antd";
import { Box, Package, Search, AlertCircle } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useDebounceValue } from "~/hooks/use-debounce";
import {
  useGetInventories,
  useGetInventoriesByIds,
} from "~/services/inventory/inventory.queries";
import { CheckType, InventoryType, METADATA_DEFAULT, PackingTypes, ContainerType } from "~/utils/constants";
import { TInventory } from "~/types/inventory";
import { useInventoryControlStore } from "./inventory-control.store";
import { MetaData } from "~/types/common";

interface InventoryControlByInventoryProps {
  onSelectedInventories?: (inventories: TInventory[]) => void;
}

export const InventoryControlByInventory: React.FC<
  InventoryControlByInventoryProps
> = ({ onSelectedInventories }) => {
  // Get state from Zustand store
  const {
    filters: { selectedClient, selectedLocation, itemType, searchTerm },
    selectedItems,
    selectionMode,
    excludedItems,
    toggleSelection,
    setSelectedItems,
    setFilters,
    deselectAll,
    setAllSelectedInventories,
  } = useInventoryControlStore();

  const [page, setPage] = useState(1);
  const [metaData, setMetaData] = useState<MetaData>(METADATA_DEFAULT);
  const [search] = useDebounceValue(searchTerm, 500);
  // Track all currently displayed items (for visible selection state)
  const [displayedItems, setDisplayedItems] = useState<TInventory[]>([]);
  // State to store all selected inventory objects
  const [selectedInventoryObjects, setSelectedInventoryObjects] = useState<
    TInventory[]
  >([]);

  const filterParams = useMemo(() => {
    const params: Record<string, any> = {
      page,
      pageSize: 20,
    };
    if (search) {
      params.search = search;
    }

    if (itemType !== "all") {
      params.itemType = itemType;
    }

    if (selectedLocation !== "all") {
      params.locationId = selectedLocation;
    }

    if (selectedClient !== "all") {
      params.clientId = selectedClient;
    }

    return params;
  }, [itemType, page, search, selectedClient, selectedLocation]);

  const { data: inventories, isLoading } = useGetInventories({
    checkParentId: CheckType.CHECK,
    ...filterParams,
  });

  // Fetch inventory details for selected IDs that are not in the current page
  const { data: selectedInventoriesData } = useGetInventoriesByIds({
    ids: selectedItems.filter(
      (id) => !inventories?.items?.some((item) => item.id === id)
    ),
    enabled: selectedItems.length > 0 && !!inventories?.items,
  });

  // Init meta data
  useEffect(() => {
    if (inventories?.meta && inventories.meta.totalItems !== metaData.totalItems) {
      setMetaData(inventories.meta);
    }
  }, [inventories?.meta]);

  // Update the selectedInventoryObjects when we get data back
  useEffect(() => {
    if (selectedInventoriesData && selectedInventoriesData.length > 0) {
      setSelectedInventoryObjects((prev) => {
        // Add only items that aren't already in the list
        const newItems = selectedInventoriesData.filter(
          (item) => !prev.some((existing) => existing.id === item.id)
        );
        return [...prev, ...newItems];
      });
    }
  }, [selectedInventoriesData]);

  // Update displayed items when inventory data changes
  useEffect(() => {
    if (inventories?.items) {
      setDisplayedItems(inventories.items);

      // Add current page items to selected inventory objects if they're selected
      const selectedCurrentPageItems = inventories.items.filter(
        (item) =>
          selectedItems.includes(item.id) &&
          !selectedInventoryObjects.some((selected) => selected.id === item.id)
      );

      if (selectedCurrentPageItems.length > 0) {
        setSelectedInventoryObjects((prev) => [
          ...prev,
          ...selectedCurrentPageItems,
        ]);
      }
    }
  }, [inventories, selectedItems, selectedInventoryObjects]);

  // Memoize selected inventories to pass to parent
  const selectedInventoriesForBulkUpdate = useMemo(() => {
    if (selectionMode === "all") {
      // In 'all' mode, we want to indicate all items are selected (not just current page)
      // Include all visible items that aren't excluded
      const visibleSelectedItems = (inventories?.items || []).filter(
        (item) => !excludedItems.includes(item.id)
      );

      // Add a special marker object to indicate all items are selected
      return [
        ...visibleSelectedItems,
        {
          id: "ALL_SELECTED",
          __allSelected: true,
          __totalCount: inventories?.meta.totalItems || 0,
        } as any,
      ];
    } else {
      // In 'partial' mode, return both currently visible selected items
      // as well as previously selected items from other pages
      const visibleSelectedItems = (inventories?.items || []).filter((item) =>
        selectedItems.includes(item.id)
      );

      // Combine with items we've previously selected but aren't currently visible
      const nonVisibleSelectedItems = selectedInventoryObjects.filter(
        (item) =>
          !displayedItems.some((displayed) => displayed.id === item.id) &&
          selectedItems.includes(item.id)
      );

      return [...visibleSelectedItems, ...nonVisibleSelectedItems];
    }
  }, [
    inventories,
    selectedItems,
    selectionMode,
    excludedItems,
    selectedInventoryObjects,
    displayedItems,
  ]);

  // Effect to pass selected inventories to parent
  useEffect(() => {
    if (onSelectedInventories) {
      onSelectedInventories(selectedInventoriesForBulkUpdate);
    }
    setAllSelectedInventories(selectedInventoriesForBulkUpdate);
  }, [
    selectedInventoriesForBulkUpdate,
    onSelectedInventories,
    setAllSelectedInventories,
  ]);

  // Handle selecting all items on current page
  const handleSelectCurrentPage = () => {
    if (!inventories?.items) return;

    const allCurrentPageIds = inventories.items.map(
      (item: TInventory) => item.id
    );
    const allSelected = allCurrentPageIds.every((id: string) =>
      selectionMode === "all"
        ? !excludedItems.includes(id)
        : selectedItems.includes(id)
    );

    if (allSelected) {
      // Deselect all on current page
      if (selectionMode === "all") {
        // In 'all' mode, add all page items to excluded
        // Switch to partial mode if we're excluding everything
        if (inventories.meta.totalItems === allCurrentPageIds.length) {
          deselectAll();
        } else {
          // Otherwise just toggle the selection
          allCurrentPageIds.forEach((id) => toggleSelection(id));
        }
      } else {
        // In 'partial' mode, remove from selectedItems
        setSelectedItems((prev) =>
          prev.filter((id) => !allCurrentPageIds.includes(id))
        );

        // Also remove from our stored inventory objects
        setSelectedInventoryObjects((prev) =>
          prev.filter((item) => !allCurrentPageIds.includes(item.id))
        );
      }
    } else {
      // Select all on current page
      if (selectionMode === "all") {
        // In 'all' mode, remove all page items from excluded
        allCurrentPageIds.forEach((id) => {
          if (excludedItems.includes(id)) {
            toggleSelection(id);
          }
        });
      } else {
        // In 'partial' mode, add to selectedItems
        setSelectedItems((prev) => [
          ...new Set([...prev, ...allCurrentPageIds]),
        ]);

        // Also add all current page items to our stored inventory objects
        const itemsToAdd = inventories.items.filter(
          (item) =>
            !selectedInventoryObjects.some(
              (selected) => selected.id === item.id
            )
        );

        if (itemsToAdd.length > 0) {
          setSelectedInventoryObjects((prev) => [...prev, ...itemsToAdd]);
        }
      }
    }
  };

  // Determine if all items on current page are selected
  const allCurrentPageSelected = useMemo(() => {
    if (!inventories?.items?.length) return false;

    return inventories.items.every((item: TInventory) =>
      selectionMode === "all"
        ? !excludedItems.includes(item.id)
        : selectedItems.includes(item.id)
    );
  }, [inventories?.items, selectedItems, selectionMode, excludedItems]);

  // Determine if some (but not all) items on current page are selected
  const someCurrentPageSelected = useMemo(() => {
    if (!inventories?.items?.length) return false;

    const anySelected = inventories.items.some((item: TInventory) =>
      selectionMode === "all"
        ? !excludedItems.includes(item.id)
        : selectedItems.includes(item.id)
    );

    return anySelected && !allCurrentPageSelected;
  }, [
    inventories?.items,
    selectedItems,
    allCurrentPageSelected,
    selectionMode,
    excludedItems,
  ]);

  // Get total selected count
  const totalSelectedCount = useMemo(() => {
    if (selectionMode === "all") {
      return (inventories?.meta.totalItems || 0) - excludedItems.length;
    } else {
      return selectedItems.length;
    }
  }, [
    selectionMode,
    inventories?.meta.totalItems,
    excludedItems.length,
    selectedItems.length,
  ]);

  // Format packing type with container type fallback
  const formatPackingType = (packingType?: PackingTypes, containerType?: ContainerType) => {
    // If it's a container type, use that instead
    if (containerType) {
      const containerMap: Record<ContainerType, string> = {
        [ContainerType.CRATE]: "Crate",
        [ContainerType.PACKAGE]: "Package",
        [ContainerType.PIECE]: "Piece",
        [ContainerType.T_Frame]: "T Frame",
        [ContainerType.SLIPCASE]: "Slipcase",
        [ContainerType.BOX]: "Box",
        [ContainerType.CUSTOM]: "Custom",
        [ContainerType.SLATCRATE]: "Slat Crate",
        [ContainerType.CBIN]: "C-Bin",
        [ContainerType.PALLET]: "Pallet",
      };
      return containerMap[containerType] || containerType;
    }

    if (!packingType) return "N/A";

    const packingMap: Record<PackingTypes, string> = {
      [PackingTypes.LOOSE]: "Loose",
      [PackingTypes.SLIPCASE]: "Slipcase",
      [PackingTypes.CB_FOLD]: "Cardboard Fold",
      [PackingTypes.SHADOW_BOX]: "Shadow Box",
      [PackingTypes.FLOATING_SHADOW_BOX]: "Floating Shadow Box",
      [PackingTypes.POLY_ONLY]: "Poly Only",
      [PackingTypes.TRAVEL_FRAME]: "Travel Frame",
      [PackingTypes.CRATE]: "Crate",
      [PackingTypes.SLATCRATE]: "Slat Crate",
      [PackingTypes.PALLET]: "Pallet",
      [PackingTypes.COMMERCIAL_BIN]: "Commercial Bin",
      [PackingTypes.CBIN]: "C-Bin",
      [PackingTypes.UNKNOWN_PACKAGING]: "Unknown Packaging",
      [PackingTypes.BOX]: "Box",
      [PackingTypes.CUSTOM]: "Custom",
      [PackingTypes.T_Frame]: "T Frame",
      [PackingTypes.BLANKET_WRAPPED]: "Blanket Wrapped",
      [PackingTypes.POLY_FOAM]: "Poly/Foam",
      [PackingTypes.POLY_FOAM_CB]: "Poly/Foam/CB",
    };

    return packingMap[packingType] || packingType;
  };

  return (
    <div className="bg-white rounded-lg border shadow-sm">
      {/* Search Bar */}
      <div className="p-4 border-b">
        <div className="relative">
          <Search
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
            size={16}
          />
          <input
            type="text"
            placeholder="Search by title, inventory ref, or artist name..."
            className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={searchTerm}
            onChange={(e) => setFilters({ searchTerm: e.target.value })}
          />
        </div>
      </div>

      {isLoading ?
        (<Spin size="large" className="p-8 flex justify-center items-center h-[60vh] w-full" />)
        : inventories?.items?.length ?
          (
            <>
              {/* Header with select all */}
              <div className="p-4 border-b bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={allCurrentPageSelected}
                        indeterminate={
                          someCurrentPageSelected && !allCurrentPageSelected
                        }
                        onChange={handleSelectCurrentPage}
                      />
                    </div>
                    <span className="text-sm text-gray-600">
                      {selectionMode === "all"
                        ? "All items selected"
                        : totalSelectedCount > 0
                          ? `${totalSelectedCount} selected`
                          : "Select all on page"}
                    </span>

                    {/* Clear Selection Button */}
                    {(selectionMode === "all" || totalSelectedCount > 0) && (
                      <Button size="small" onClick={deselectAll} danger>
                        Clear Selection
                      </Button>
                    )}
                  </div>
                  <span className="text-sm text-gray-500">
                    {inventories.meta.totalItems} total items
                  </span>
                </div>

                {/* Selection Mode Banner */}
                {selectionMode === "all" && (
                  <div className="mt-2 p-2 bg-blue-50 rounded-lg border border-blue-100 flex items-center space-x-2">
                    <AlertCircle className="text-blue-600" size={16} />
                    <span className="text-xs text-blue-700">
                      All {inventories.meta.totalItems} items are selected.
                      {excludedItems.length > 0 &&
                        ` (${excludedItems.length} items excluded)`}
                    </span>
                  </div>
                )}
              </div>

              {/* Selection Stats */}
              <div className="p-4 border-b flex items-center justify-between bg-blue-50">
                <div className="flex items-center space-x-4">
                  <Checkbox
                    checked={allCurrentPageSelected}
                    indeterminate={someCurrentPageSelected && !allCurrentPageSelected}
                    onChange={handleSelectCurrentPage}
                    className="text-blue-600"
                  />
                  <div>
                    <span className="text-sm font-medium text-gray-700">
                      {totalSelectedCount} item{totalSelectedCount !== 1 ? "s" : ""}{" "}
                      selected
                      {selectionMode === "all" ? " (all matching items)" : ""}
                    </span>
                    {selectedItems.length > 0 && selectedItems.length < 5 && (
                      <span className="ml-2 text-xs text-gray-500">
                        including items from other pages
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex space-x-2">
                  {selectionMode === "all" && (
                    <Button size="small" onClick={deselectAll} danger>
                      Clear Selection
                    </Button>
                  )}
                </div>
              </div>

              {/* Inventory List */}
              <div className="divide-y divide-gray-200">
                {inventories.items.map((item: TInventory) => {
                  const isSelected =
                    selectionMode === "all"
                      ? !excludedItems.includes(item.id)
                      : selectedItems.includes(item.id);

                  return (
                    <div
                      key={item.id}
                      className={`p-4 hover:bg-gray-50 transition-colors ${isSelected ? "bg-blue-50" : ""
                        }`}
                      onClick={() => toggleSelection(item.id)}
                    >
                      <div className="flex items-center space-x-4">
                        <div onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={isSelected}
                            onChange={() => toggleSelection(item.id)}
                          />
                        </div>

                        <div className="flex-shrink-0">
                          {item.itemType === InventoryType.PACKAGE ? (
                            <Box className="text-blue-600" size={20} />
                          ) : (
                            <Package className="text-green-600" size={20} />
                          )}
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {item.title || "Untitled"}
                              </p>
                              <p className="text-xs text-gray-500">
                                {item.inventoryRef}
                                {item.clientRefNumber && ` • ${item.clientRefNumber}`}
                                {" • "}{item.artistName || "Unknown Artist"}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-xs text-gray-500">
                                Client:{" "}
                                {item.client?.companyName ||
                                  `${item.client?.firstName} ${item.client?.lastName}` ||
                                  "Unknown"}
                              </p>
                              <p className="text-xs text-gray-500">
                                Location: {item.location?.name || "Unknown"}
                              </p>
                            </div>
                          </div>

                          <div className="mt-1 flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                              <span
                                className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${item.status === "AVAILABLE_ON_HAND"
                                  ? "bg-green-100 text-green-800"
                                  : item.status === "AVAILABLE_LIVE"
                                    ? "bg-blue-100 text-blue-800"
                                    : item.status === "NO_POSSESSION"
                                      ? "bg-orange-100 text-orange-800"
                                      : "bg-gray-100 text-gray-800"
                                  }`}
                              >
                                {item.status?.replace(/_/g, " ") || "Unknown"}
                              </span>
                              <span className="text-xs text-gray-500">
                                {item.itemType}
                              </span>
                              <span className="text-xs text-gray-500">
                                • {formatPackingType(item.packingType, item.containerType)}
                              </span>
                            </div>

                            {item.length && item.width && item.height && (
                              <span className="text-xs text-gray-500">
                                {item.length}" × {item.width}" × {item.height}"
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )
          : (
            <div className="h-[64vh] flex items-center justify-center bg-white rounded-lg border shadow-sm p-6">
              <Empty description="No inventory items found" />
            </div>
          )
      }

      {/* Pagination */}
      {metaData.totalItems > 0 && (
        <div className="p-4 border-t bg-gray-50">
          <Pagination
            current={page}
            pageSize={20}
            total={metaData.totalItems}
            onChange={(page) => setPage(page)}
            showSizeChanger={false}
            showTotal={(total) => `Total ${total} items`}
          />
        </div>
      )}
    </div>
  );
};
